import { z } from "zod/v4";
import { ClassificationReason } from "./classification.ts";
import { EmployeeEntity, ProjectPermissionViewEntity } from "./entities.ts";
import {
  AggregateId,
  CardId,
  CardLanguage,
  Comment,
  CompanyName,
  DepartmentName,
  Email,
  EmployeeName,
  FirstName,
  Formality,
  LastName,
  LdapGroup,
  ProjectId,
  ProjectInfobox,
  ProjectName,
  PublicCardId,
  SalutationKind,
  TimestampUtc,
  Title,
  UidNumber,
} from "./primitives.ts";

/*
 * This file contains all types dedicated to data exchange with our Frontend, i.e.
 * - request and response payloads
 * - other types only used for this purpose
 * - helper functions only needed in this file (should not be exported)
 */

// The outer optional makes omission visible to JSON Schema converters.
const mapFalsyToUndefined = <T extends z.ZodType>(parser: T) =>
  z.preprocess((value) => value || undefined, parser).optional();

export const SenderId = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("employee"),
    uidNumber: UidNumber,
  }),
  z.object({
    type: z.literal("email"),
    email: Email,
  }),
]);
export type SenderId = z.infer<typeof SenderId>;

export const GENERIC_SENDER_EMAIL = "xmas@tngtech.com";

export const CardsListItem = z.object({
  id: AggregateId,
  firstName: FirstName,
  lastName: LastName,
  projectId: ProjectId,
  companyName: CompanyName.optional(),
  departmentName: DepartmentName.optional(),
  senderId: SenderId.optional(),
  senderDisplayName: z.string().optional(),
  manualDispatch: z.boolean(),
  email: z.email().optional(),
  comment: z.string().optional(),
  hasContactDetailsWarning: z.boolean(),
  senderUnknown: z.boolean(),
  numberOfSignatures: z.number().nonnegative().optional(),
  signatures: z
    .object({ uidNumber: UidNumber, name: EmployeeName })
    .array()
    .optional(),
  isSignedByUser: z.boolean(),
  language: CardLanguage,
  title: Title.optional(),
  salutation: SalutationKind,
  formality: Formality,
  receivesGift: z.boolean(),
  created: z
    .object({
      at: TimestampUtc,
      by: z.string(),
    })
    .optional(),
  lastChanged: z
    .object({
      at: TimestampUtc,
      by: z.string(),
    })
    .optional(),
  shared: z.boolean(),
  responsibleProjectId: ProjectId,
});
export type CardsListItem = z.infer<typeof CardsListItem>;

export type ListCardsResponse = {
  items: CardsListItem[];
};

const MySignedCardItem = z.object({
  id: AggregateId,
  firstName: FirstName,
  lastName: LastName,
  companyName: CompanyName.optional(),
  projectId: ProjectId,
  projectName: ProjectName,
  senderDisplayName: z.string().optional(),
});
export type MySignedCardItem = z.infer<typeof MySignedCardItem>;

export const MySignedCardsResponse = z.object({
  cards: z.array(MySignedCardItem),
});
export type MySignedCardsResponse = z.infer<typeof MySignedCardsResponse>;

export const GetCardSignaturesResponse = z.object({
  signatures: z.object({ uidNumber: UidNumber }).array(),
  firstName: FirstName,
  lastName: LastName,
});
export type GetCardSignaturesResponse = z.infer<
  typeof GetCardSignaturesResponse
>;

const EmployeeContact = z.object({
  name: EmployeeName,
  email: Email.optional(),
});
const GroupContact = z.object({
  name: LdapGroup,
  email: Email.optional(),
});
const ListCardsPermissionResponse = z.object({
  individuals: EmployeeContact.array(),
  groups: GroupContact.array(),
});
export type ListCardsPermissionResponse = z.infer<
  typeof ListCardsPermissionResponse
>;

export const ListCardsResponseWithProjectName = z.object({
  items: CardsListItem.array(),
  projectDisplayName: z.string(),
  projectInfoBox: z.string().optional(),
  hiddenFields: z.string().array(),
  projectShepherds: ListCardsPermissionResponse,
  hasPermissions: z.boolean(),
});
export type ListCardsResponseWithProjectName = z.infer<
  typeof ListCardsResponseWithProjectName
>;
export const ListCardsNoPermissionResponse = z.object({
  projectShepherds: ListCardsPermissionResponse,
  projectDisplayName: z.string(),
  hasPermissions: z.boolean(),
});
export type ListCardsNoPermissionResponse = z.infer<
  typeof ListCardsNoPermissionResponse
>;

export const EditProjectResponse = z.object({
  projectShepherds: ListCardsPermissionResponse,
});
export type EditProjectResponse = z.infer<typeof EditProjectResponse>;

export const GetProjectsForSidebarResponse = z
  .object({
    id: AggregateId,
    displayName: ProjectName,
    defaultSenderId: SenderId.optional(),
  })
  .array();
export type GetProjectsForSidebarResponse = z.infer<
  typeof GetProjectsForSidebarResponse
>;

export const ListActionsRequest = z.object({
  projectId: ProjectId,
});
export type ListActionsRequest = z.infer<typeof ListActionsRequest>;

export const GlobalActionsResponse = z.object({
  actions: z.string().nonempty().array(),
  projects: z.record(ProjectId, z.string().nonempty().array()),
});
export type GlobalActionsResponse = z.infer<typeof GlobalActionsResponse>;

export const ListDepartmentsResponse = z.object({
  departmentNames: DepartmentName.array(),
});
export type ListDepartmentsResponse = z.infer<typeof ListDepartmentsResponse>;

export const CreateCardRequest = z
  .object({
    projectId: ProjectId,
    departmentName: mapFalsyToUndefined(DepartmentName.optional()),
    recipient: z.object({
      firstName: FirstName,
      lastName: LastName,
      preferredLanguage: CardLanguage,
      salutationKind: SalutationKind,
      email: mapFalsyToUndefined(Email.optional()),
      title: mapFalsyToUndefined(Title.optional()),
      companyName: mapFalsyToUndefined(CompanyName.optional()),
    }),
    formality: Formality,
    receivesGift: z.boolean(),
    manualDispatch: z.boolean(),
    senderId: mapFalsyToUndefined(SenderId.optional()),
    comment: mapFalsyToUndefined(Comment.optional()),
  })
  .refine((data) => data.manualDispatch || data.recipient.email, {
    params: { translationKey: "emailRequiredForAutomaticDispatch" },
    path: ["recipient", "email"],
  })
  .refine((data) => !data.manualDispatch || data.senderId, {
    params: { translationKey: "senderRequiredForManualDispatch" },
    path: ["senderId"],
  })
  .refine((data) => !(data.manualDispatch && data.senderId?.type === "email"), {
    params: { translationKey: "emailSenderNotPossibleForManualDispatch" },
    path: ["senderId"],
  })
  .refine(
    (data) =>
      !(
        data.recipient.preferredLanguage === "en" &&
        data.formality === "semi-formal"
      ),
    {
      params: { translationKey: "noSemiFormalAvailable" },
      path: ["formality"],
    },
  );
export type CreateCardRequest = z.infer<typeof CreateCardRequest>;

export const CheckShareCardEmailRequest = z.object({
  projectId: ProjectId,
  email: Email,
});
export type CheckShareCardEmailRequest = z.infer<
  typeof CheckShareCardEmailRequest
>;

export const ShareCardRequest = z.object({
  responsibleProjectId: ProjectId,
});
export type ShareCardRequest = z.infer<typeof ShareCardRequest>;

export const ShareCardInput = ShareCardRequest.safeExtend({
  cardId: CardId,
  sharedProjectId: ProjectId,
});
export type ShareCardInput = z.infer<typeof ShareCardInput>;

export const ShareCardResponse = z.object({
  id: AggregateId,
  projectId: ProjectId,
  responsibleProjectId: ProjectId,
  departmentName: mapFalsyToUndefined(DepartmentName.optional()),
  recipient: z.object({
    firstName: FirstName,
    lastName: LastName,
    preferredLanguage: CardLanguage,
    salutationKind: SalutationKind,
    email: mapFalsyToUndefined(Email.optional()),
    title: mapFalsyToUndefined(Title.optional()),
    companyName: mapFalsyToUndefined(CompanyName.optional()),
  }),
  formality: Formality,
  receivesGift: z.boolean(),
  manualDispatch: z.boolean(),
  senderId: mapFalsyToUndefined(SenderId.optional()),
  comment: mapFalsyToUndefined(Comment.optional()),
});
export type ShareCardResponse = z.infer<typeof ShareCardResponse>;

export const CreateCardResponse = z.object({
  id: AggregateId,
  publicId: PublicCardId,
});

export type CreateCardResponse = z.infer<typeof CreateCardResponse>;

export const EditCardRequest = CreateCardRequest.safeExtend({
  id: AggregateId,
});
export type EditCardRequest = z.infer<typeof EditCardRequest>;

export const ContactDetailsWarning = z.object({
  reason: ClassificationReason.optional(),
  justification: z.string().optional(),
});
export type ContactDetailsWarning = z.infer<typeof ContactDetailsWarning>;

export const EditCardViewResponse = EditCardRequest.safeExtend({
  hasContactDetailsWarning: z.boolean(),
  contactDetailsWarning: ContactDetailsWarning.optional(),
  isSharedByOtherProjects: z.boolean().optional(),
});
export type EditCardViewResponse = z.infer<typeof EditCardViewResponse>;

export type GetProjectResponse = {
  project: ProjectPermissionViewEntity;
};

const PermissionsInRequest = z.object({
  group: LdapGroup.array().default([]),
  individual: UidNumber.array().default([]),
});

export const CreateProjectRequest = z.object({
  displayName: ProjectName,
  projectShepherds: PermissionsInRequest.refine(
    (data) => data.group.length + data.individual.length > 0,
    {
      params: { translationKey: "selectAtLeastOneShepherd" },
      path: ["group"],
    },
  ),
  projectMembers: PermissionsInRequest,
  projectSigners: PermissionsInRequest,
  infoBox: mapFalsyToUndefined(ProjectInfobox.optional()),
  defaultSenderId: SenderId.optional(),
});
export type CreateProjectRequest = z.infer<typeof CreateProjectRequest>;

export const ManageProjectRequest = CreateProjectRequest.omit({
  defaultSenderId: true,
}).safeExtend({
  id: ProjectId,
});
export type ManageProjectRequest = z.infer<typeof ManageProjectRequest>;

export const UniqueCheckResponse = z.object({
  isUnique: z.boolean(),
  cardInOtherProject: z
    .object({
      cardId: CardId,
      responsibleProjectId: ProjectId,
    })
    .optional(),
});
export type UniqueCheckResponse = z.infer<typeof UniqueCheckResponse>;

export type GetEmployeesResponse = { employees: EmployeeEntity[] };

export type GetEligibleSendersResponse = { senderIds: SenderId[] };

export const GlobalSearchCardsItem = z.object({
  id: AggregateId,
  firstName: FirstName,
  lastName: LastName,
  projectId: ProjectId,
  projectDisplayName: ProjectName.optional(),
  companyName: CompanyName.optional(),
  departmentName: DepartmentName.optional(),
  senderId: SenderId.optional(),
  senderDisplayName: z.string().optional(),
  manualDispatch: z.boolean(),
  email: Email.optional(),
  comment: z.string().optional(),
  language: CardLanguage,
  title: Title.optional(),
  salutation: SalutationKind,
  formality: Formality,
  receivesGift: z.boolean(),
  signatures: z.object({ uidNumber: UidNumber, name: EmployeeName }).array(),
});
export type GlobalSearchCardsItem = z.infer<typeof GlobalSearchCardsItem>;

export const GlobalSearchCardsResponse = z.object({
  cards: GlobalSearchCardsItem.array(),
  hasMore: z.boolean(),
});
export type GlobalSearchCardsResponse = z.infer<
  typeof GlobalSearchCardsResponse
>;

export const CardErrorType = z.enum([
  "missingSenderManualDispatch",
  "missingSenderAutomaticDispatch",
  "emailSenderManualDispatch",
  "xmasSenderAutomaticDispatch",
  "senderUnknown",
  "senderNotSigner",
  "duplicateEmail",
  "duplicateName",
  "contactDetailsInconsistent",
]);
export type CardErrorType = z.infer<typeof CardErrorType>;

export const CardErrorsItem = GlobalSearchCardsItem.pick({
  id: true,
  firstName: true,
  lastName: true,
  email: true,
  projectId: true,
  projectDisplayName: true,
  senderDisplayName: true,
}).extend({
  errors: CardErrorType.array().min(1),
});
export type CardErrorsItem = z.infer<typeof CardErrorsItem>;

export const CardErrorsResponse = z.object({
  cards: CardErrorsItem.array(),
  projectsWithoutShepherd: z
    .object({ id: ProjectId, displayName: ProjectName })
    .array()
    .default([]),
});
export type CardErrorsResponse = z.infer<typeof CardErrorsResponse>;

export const ManualDispatchCardItem = z.object({
  id: AggregateId,
  projectId: ProjectId,
  projectName: ProjectName.optional(),
  firstName: FirstName,
  lastName: LastName,
  email: Email.optional(),
  subject: z.string(),
  body: z.string(),
  manuallySentAt: TimestampUtc.nullable(),
});
export type ManualDispatchCardItem = z.infer<typeof ManualDispatchCardItem>;

export const ManualDispatchOverviewResponse = z.object({
  activatedAt: TimestampUtc.nullable(),
  cards: ManualDispatchCardItem.array(),
});
export type ManualDispatchOverviewResponse = z.infer<
  typeof ManualDispatchOverviewResponse
>;
